
import { mapGetters } from 'vuex';
import { mapState } from 'vuex';
import { gsap } from 'gsap';
import reactionAPI from '../api/reactions';
import filesAPI from '../api/files';
import hasCurrentFirmUuid from '../mixins/hasCurrentFirmUuid';
import filesize from 'filesize';

export default {
  name: 'MessageSingle',

  mixins: [hasCurrentFirmUuid],

  props: {
    message: {},
    reactions: [],
    conversationUuid: null,
    groupedTogether: false,
  },

  data() {
    return {
      availableReactions: [
        {
          uuid: '1r',
          type: 'like',
        },
        {
          uuid: '2r',
          type: 'love',
        },
        {
          uuid: '3r',
          type: 'laughing',
        },
        {
          uuid: '4r',
          type: 'surprised',
        },
      ],
      showReaction: false,
      userHasReacted: false,
    };
  },

  computed: {
    ...mapGetters('user', ['currentAccountUuid']),
    ...mapState('user', ['user']),

    isCurrentUser() {
      return this.message.author?.uuid === this.currentAccountUuid;
    },

    authorName() {
      return this.message.author?.name ?? 'Deleted user';
    },

    hideAuthor() {
      return !this.groupedTogether;
    },
  },

  mounted() {
    this.q = gsap.utils.selector(this.$el);

    this.$nextTick(() => {
      if (this.isCurrentUser) {
        gsap.set(this.q('.reaction-select-element'), { x: 200 });
      } else {
        gsap.set(this.q('.reaction-select-element'), { x: -200 });
      }
    });
  },

  methods: {
    isOnline(a) {
      // need to add a check to see if user is online

      return true;
    },

    checkIfOnline() {
      return this.isOnline ? 'bg-p-green' : 'bg-p-gray';
    },

    messageOver() {
      gsap.killTweensOf(this.q('.reaction-select-element'));

      if (this.isCurrentUser) {
        gsap.to(this.q('.reaction-select-element'), {
          x: 0,
          stagger: 0.1,
        });
      } else {
        gsap.to(this.q('.reaction-select-element'), {
          x: 0,
          stagger: -0.1,
        });
      }
    },

    messageLeave() {
      gsap.killTweensOf(this.q('.reaction-select-element'));

      if (this.isCurrentUser) {
        gsap.to(this.q('.reaction-select-element'), {
          x: 250,
          stagger: -0.1,
          delay: 0.25,
        });
      } else {
        gsap.to(this.q('.reaction-select-element'), {
          x: -250,
          stagger: 0.1,
          delay: 0.25,
        });
      }
    },

    addReaction(o) {
      let index = this.message.reactions.findIndex((item) => item && item.uuid === o.event.uuid);

      let u = {};

      u.uuid = this.user.uuid;
      u.name = this.user.first_name + ' ' + this.user.first_name;

      this.message.reactions[index].users.push(u);
    },

    reactionClicked(event) {
      let reactionType = null;
      let reactionUUID = null;

      if (Array.isArray(event)) {
        // event is an array of reaction objects from ui-reaction-display
        const userReaction = event.find((r) => r.account_uuid === this.currentAccountUuid);
        if (userReaction) {
          reactionUUID = userReaction.uuid;
          reactionType = userReaction.reaction;
        } else {
          // User hasn't reacted yet but clicked on an existing reaction
          // Get the reaction type from the first item (all items are the same type)
          reactionType = event[0]?.reaction;
        }
      } else {
        // if event is an object { type: 'like', uuid: '...' } from ui-reaction-select
        reactionType = event.type;

        // Check if user already reacted with this type
        if (this.message?.reactions) {
          const existing = this.message.reactions.find(
            (r) => r.reaction === reactionType && r.account_uuid === this.currentAccountUuid
          );
          if (existing) {
            reactionUUID = existing.uuid;
          }
        }
      }

      if (reactionUUID) {
        reactionAPI.remove(reactionUUID).then(() => {
          const index = this.message.reactions.findIndex((e) => e && e.uuid === reactionUUID);
          if (index !== -1) {
            this.message.reactions.splice(index, 1);
          }
        });
      } else if (reactionType) {
        const data = {
          reaction: reactionType.toLowerCase(),
          account_uuid: this.currentAccountUuid,
          reactable_type: 'message',
          reactable_uuid: this.message.uuid,
        };
        // send to the server
        reactionAPI.react(data).then((rsp) => {
          if (this.message.reactions) {
            this.message.reactions.push(rsp.data.data);
          } else {
            this.message.reactions = [rsp.data.data];
          }
        });
      }
    },

    getSize(size) {
      return filesize(size);
    },

    canDownloadFile(file) {
      return file.status === 'uploaded';
    },

    cannotDownloadFile(file) {
      return !this.canDownloadFile(file);
    },

    downloadFile(file) {
      if (this.cannotDownloadFile(file)) {
        return;
      }

      filesAPI.downloadFile(file);
    },
  },
};
